import { CircleInfoIcon } from "@xola/icons";
import { Alert, flash, Label, SubmitButton } from "@xola/ui-kit";
import clsx from "clsx";
import { Loader, SecondaryButton, SwitchField } from "common";
import { useFormikContext } from "formik";
import { isEmpty, omit, uniqBy } from "lodash";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
    CouponType,
    Experience,
    FeatureRole,
    LineItemCode,
    Package,
    PaymentMethod,
    PurchaseRule,
    PurchaseRuleLink,
    PurchaseRuleLinkEntityObject,
} from "types";
import { number, object } from "yup";
import { RadioInput } from "../../Common/RadioInput";
import { XolaFormik } from "../../Common/XolaFormik";
import { ContentLayout } from "../../Layout/ContentLayout";
import { DownDepositSelector } from "../pricing/DownDepositSelector";
import { BasicTerm, PackagePricingValues } from "./EditPackageBasicDiscount";
import { getPricingTermDiscount, updateBasicTermPurchaseRule, updatePurchaseRulePayload } from "./PackagePricingHelper";
import {
    DiscountPricingValue,
    getPackagePurchaseRuleLinkPayload,
    getTemplateId,
    TermsType,
    useLineItemTemplateByCode,
} from "./PricingSelectorHelper";
import { api } from "@/api/api";
import { AdvancedTerm } from "@/components/Product/Packages/EditPackageAdvanceDiscount";
import { useSeller } from "@/hooks/useAccount";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useCollection } from "@/hooks/useCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { getSelectedExperiencesByIds } from "@/models/Experience";
import { getProductIdsInPackage } from "@/models/Package";
import { isOnFlexSubscription } from "@/models/PartnerFeeFormula";
import { getPurchaseRuleForExperience } from "@/models/PurchaseRule";
import { getExistingPurchaseRule, getPurchaseRulesFromPurchaseRuleLinks } from "@/models/PurchaseRuleLink";
import { hasFeature } from "@/models/Seller";

const validationSchema = object().shape({
    basicDiscount: object().shape({
        discount: number().when("termType", {
            is: TermsType.BASIC,
            then: number().positive().max(100).min(0).required("is required"),
        }),
    }),
    downDeposit: object()
        .default(null)
        .nullable()
        .shape({
            amount: number()
                .required("is required")
                .typeError("Should be valid number")
                .min(0, "should be a positive integer"),
            days: number()
                .required("is required")
                .integer("should be a positive integer")
                .typeError("should be a positive integer")
                .min(0, "should be a positive integer"),
        }),
});

export const EditPackagePricingPage = ({ pkg }: { pkg: Package }) => {
    const { data: purchaseRuleLinks, isLoading: isLoadingPurchaseRuleLink } = useCollection<PurchaseRuleLink>(
        "purchaseRuleLinks",
        {
            "entities.id": pkg.id,
            "entities.object": PurchaseRuleLinkEntityObject.PACKAGE,
            expand: "purchaseRule",
        },
    );

    if (isLoadingPurchaseRuleLink) {
        return <Loader />;
    }

    return <EditPackagePricing pkg={pkg} purchaseRuleLinks={purchaseRuleLinks} />;
};

const EditPackagePricing = ({ pkg, purchaseRuleLinks }: { pkg: Package; purchaseRuleLinks: PurchaseRuleLink[] }) => {
    const history = useHistory();
    const seller = useSeller();
    const invalidate = useInvalidate();

    const { data: discountTemplate } = useLineItemTemplateByCode(LineItemCode.PACKAGE_DISCOUNT);
    const { data: experiences, isFetching } = useAutoFetchCollection<Experience>("experiences");
    const hasRealtimeUpdateEnabled = hasFeature(seller, FeatureRole.REALTIME_UPDATES);

    const purchaseRules = getPurchaseRulesFromPurchaseRuleLinks(purchaseRuleLinks);
    const [purchaseRuleState, setPurchaseRuleState] = useState(purchaseRules);
    const [basicDiscount, setBasicDiscount] = useState(getPricingTermDiscount(purchaseRuleState, purchaseRuleLinks));
    const [isAdvanceSelected, setIsAdvanceSelected] = useState(basicDiscount.termType === TermsType.ADVANCED);

    const initialValues: PackagePricingValues = {
        paymentMethod: PaymentMethod.CREDIT_CARD,
        downDeposit: pkg.downDeposit,
        basicDiscount: {
            discount: basicDiscount.discount ?? 0,
            termType: basicDiscount.termType,
            includeAddons: basicDiscount.includeAddons,
        },
    };

    const selectedExperienceIds: ID[] = getProductIdsInPackage(pkg);
    const selectedExperiences = getSelectedExperiencesByIds(selectedExperienceIds, experiences);

    useEffect(() => {
        const purchaseRules = getPurchaseRulesFromPurchaseRuleLinks(purchaseRuleLinks);
        if (purchaseRules.length > 0) {
            setPurchaseRuleState(purchaseRules);
            if (!isAdvanceSelected) {
                setBasicDiscount(getPricingTermDiscount(purchaseRules, purchaseRuleLinks));
            }
        }
    }, [purchaseRuleLinks, isAdvanceSelected]);

    const handleDemographicChange = (discountPricingValues: DiscountPricingValue[], experience: Experience) => {
        const purchaseRule = getPurchaseRuleForExperience(purchaseRuleState, experience.id);
        if (purchaseRule) {
            updatePurchaseRule(purchaseRule, discountPricingValues, experience.id, purchaseRule.meta?.addons);
        }
    };

    const handleAddOnChange = (
        discountPricingValues: DiscountPricingValue[],
        experience: Experience,
        isEnabled?: boolean,
    ) => {
        const purchaseRule = getPurchaseRuleForExperience(purchaseRuleState, experience.id);
        if (purchaseRule && !isEmpty(discountPricingValues)) {
            updatePurchaseRule(purchaseRule, discountPricingValues, experience.id, isEnabled);
        } else if (purchaseRule) {
            // update addon enabled state
            setPurchaseRuleState((previousPurchaseRuleState) =>
                previousPurchaseRuleState.map((purchaseRuleItr) =>
                    purchaseRuleItr.id !== purchaseRule.id || purchaseRuleItr.meta?.subProductId !== experience.id
                        ? purchaseRuleItr
                        : { ...purchaseRuleItr, meta: { ...purchaseRuleItr.meta, addons: isEnabled } },
                ),
            );
        }
    };

    const updatePurchaseRule = (
        purchaseRule: PurchaseRule,
        discountPricingValues: DiscountPricingValue[],
        productId: ID,
        isEnabled = true,
    ) => {
        let purchaseRuleStates: PurchaseRule[] = [];

        const templateIds = new Set(discountPricingValues.map(({ action }) => getTemplateId(action)));

        // Old actions either demographics or addons that already exist and currently not updating
        const filteredActions = purchaseRule.actions.filter((actionItr) => {
            const actionTemplateId = getTemplateId(actionItr) ?? "";
            return !templateIds.has(actionTemplateId);
        });

        // New actions either demographics or addons that we currently updating
        const updatedActions = discountPricingValues
            .filter(({ action }) => (!action.id && !action._remove) || action.id)
            .map((value) => value.action);

        // already saved actions with id
        const oldUniqActions = uniqBy(
            [...updatedActions.filter((action) => action.id), ...filteredActions.filter((action) => action.id)],
            "id",
        );

        // new actions without ids
        const newActions = [
            ...updatedActions.filter((action) => !action.id),
            ...filteredActions.filter((action) => !action.id),
        ];

        // Other purchase rules except this current experience rules
        const purchaseRules = purchaseRuleState.filter(({ id }) => {
            return purchaseRule.id !== id || purchaseRule.meta?.subProductId !== productId;
        });

        purchaseRuleStates = [
            ...purchaseRules,
            {
                ...purchaseRule,
                meta: {
                    ...purchaseRule.meta,
                    subProductId: productId,
                    addons: isEnabled,
                },
                actions: [...newActions, ...oldUniqActions],
            },
        ];

        setPurchaseRuleState(purchaseRuleStates);
    };

    const handleCancel = () => {
        history.push("/products/packages");
    };

    const handleSubmit = async (values: PackagePricingValues) => {
        if (values.basicDiscount.termType === TermsType.BASIC) {
            const updatedPurchaseRules = updateBasicTermPurchaseRule(
                values,
                selectedExperiences,
                purchaseRuleState,
                discountTemplate,
            );
            setPurchaseRuleState(updatedPurchaseRules);
            handlePurchaseRules(values, updatedPurchaseRules, purchaseRules);
        } else {
            handlePurchaseRules(values, purchaseRuleState, purchaseRules);
        }

        // Updating package in the end as this will invalidate the purchaseRuleLinks in pusher.ts
        await updatePackage(omit(values, "basicDiscount"));

        if (!hasRealtimeUpdateEnabled) {
            invalidate("packages", pkg.id);
        }

        flash.show({ text: "Successfully updated package", color: "success" });
    };

    const handlePurchaseRules = async (
        values: PackagePricingValues,
        purchaseRules?: PurchaseRule[],
        originalPurchaseRules?: PurchaseRule[],
    ) => {
        const purchaseRulePromises: Array<Promise<PurchaseRule>> = [];

        for (const purchaseRule of purchaseRules ?? purchaseRuleState) {
            const originalPurchaseRule = originalPurchaseRules?.find((rule) => rule.id === purchaseRule.id);
            purchaseRulePromises.push(
                savePurchaseRule(updatePurchaseRulePayload(selectedExperiences, purchaseRule, originalPurchaseRule)),
            );
        }

        await Promise.all(purchaseRulePromises).then(async (purchaseRules) => {
            purchaseRules.map(async (purchaseRule) => {
                const purchaseRuleLink = getExistingPurchaseRule(purchaseRuleLinks, purchaseRule);
                const updatedPurchaseLink = await savePurchaseRuleLink(
                    getPackagePurchaseRuleLinkPayload(
                        seller,
                        pkg,
                        purchaseRule,
                        purchaseRuleLink,
                        values.basicDiscount,
                    ),
                );

                invalidate("purchaseRuleLinks", updatedPurchaseLink.id);
            });
        });
    };

    if (isFetching && experiences?.length === 0) {
        return <Loader />;
    }

    return (
        <ContentLayout isWide>
            <XolaFormik
                enableReinitialize
                initialValues={{ ...initialValues, ...pkg, isFormDirty: false }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, handleSubmit, values, setFieldValue, errors }) => {
                    return (
                        <>
                            {isFetching && experiences?.length > 0 && (
                                <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-white bg-opacity-80">
                                    <Loader />
                                </div>
                            )}
                            <h2 className="mt-5">Pricing</h2>
                            <div className="divide-y divide-gray-light">
                                <TermType
                                    isAdvanceSelected={isAdvanceSelected}
                                    setIsAdvanceSelected={setIsAdvanceSelected}
                                />

                                <ContentLayout.Section
                                    title="Terms"
                                    description="Set the discount terms that incentivize customers to purchase this package"
                                >
                                    <Alert color="warning" className="mt-2 mb-2 py-4 px-6">
                                        <div className="flex items-center space-x-2">
                                            <CircleInfoIcon size="medium" className="flex-shrink-0 align-middle" />
                                            <p>
                                                Discounts set up here will stack on top of any price variations you have
                                                established elsewhere (if applicable), such as tiered pricing, schedule
                                                based pricing variations, etc.
                                            </p>
                                        </div>
                                    </Alert>

                                    {!isAdvanceSelected && <BasicTerm />}
                                    <div className={clsx("mt-6 space-y-1", isAdvanceSelected && "mb-6")}>
                                        <Label>Coupon Discounts</Label>
                                        <SwitchField
                                            isChecked={
                                                !!values.allowedCouponTypes && values.allowedCouponTypes.length > 0
                                            }
                                            onChange={(value: boolean) => {
                                                if (value) {
                                                    setFieldValue("allowedCouponTypes", [
                                                        CouponType.DISCOUNT,
                                                        CouponType.UNLOCK,
                                                    ]);
                                                } else {
                                                    setFieldValue("allowedCouponTypes", []);
                                                }
                                            }}
                                        >
                                            Allow customers to apply coupons on top of the package discount
                                        </SwitchField>
                                    </div>

                                    {isAdvanceSelected && (
                                        <AdvancedTerm
                                            pkg={pkg}
                                            experiences={experiences}
                                            purchaseRules={purchaseRuleState}
                                            onChangeDemographicDiscount={handleDemographicChange}
                                            onChangeAddOnDiscount={handleAddOnChange}
                                        />
                                    )}
                                </ContentLayout.Section>

                                {!isOnFlexSubscription(seller) && (
                                    <ContentLayout.Section
                                        title="Payment Method"
                                        description="Choose to collect payment or not during checkout."
                                    >
                                        <RadioInput
                                            name="paymentMethod"
                                            checked={values.paymentMethod === PaymentMethod.CREDIT_CARD}
                                            onChange={() => {
                                                setFieldValue("paymentMethod", PaymentMethod.CREDIT_CARD);
                                            }}
                                        >
                                            Credit Card payment required on checkout
                                        </RadioInput>
                                        <RadioInput
                                            name="paymentMethod"
                                            checked={values.paymentMethod === PaymentMethod.LATER}
                                            onChange={() => {
                                                setFieldValue("paymentMethod", PaymentMethod.LATER);
                                                setFieldValue("downDeposit", null);
                                            }}
                                        >
                                            No payment required on checkout
                                        </RadioInput>
                                    </ContentLayout.Section>
                                )}

                                <ContentLayout.Section
                                    title="Down Deposit"
                                    description="Provides the customer with the option to pay a partial amount during checkout."
                                >
                                    <DownDepositSelector
                                        experience={pkg}
                                        value={values}
                                        errors={errors}
                                        onChange={setFieldValue}
                                    />
                                </ContentLayout.Section>
                                <ContentLayout.Footer preferenceSidebar>
                                    <SubmitButton
                                        isLoading={isSubmitting}
                                        disabled={isSubmitting}
                                        onClick={handleSubmit}
                                    >
                                        Save
                                    </SubmitButton>
                                    <SecondaryButton type="reset" onClick={handleCancel}>
                                        Cancel
                                    </SecondaryButton>
                                </ContentLayout.Footer>
                            </div>
                        </>
                    );
                }}
            </XolaFormik>
        </ContentLayout>
    );
};

const updatePackage = async (payload: Partial<Package>) => {
    return api.put(`/packages/${payload.id}`, payload);
};

const savePurchaseRule = async (purchaseRule: PurchaseRule) => {
    const { data: savedPurchaseRule } = await api.put<PurchaseRule>(`/purchaseRules/${purchaseRule.id}`, purchaseRule);

    return savedPurchaseRule;
};

const savePurchaseRuleLink = async (purchaseRuleLink: PurchaseRuleLink) => {
    if (purchaseRuleLink.id) {
        const { data: savedPurchaseRuleLink } = await api.put<PurchaseRuleLink>(
            `/purchaseRuleLinks/${purchaseRuleLink.id}`,
            purchaseRuleLink,
        );
        return savedPurchaseRuleLink;
    }

    const { data: savedPurchaseRuleLink } = await api.post<PurchaseRuleLink>(`/purchaseRuleLinks`, purchaseRuleLink);

    return savedPurchaseRuleLink;
};

export const TermType = ({
    isAdvanceSelected,
    setIsAdvanceSelected,
}: {
    isAdvanceSelected: boolean;
    setIsAdvanceSelected: (isAdvanceSelected: boolean) => void;
}) => {
    const { setFieldValue } = useFormikContext<PackagePricingValues>();

    return (
        <ContentLayout.Section
            title="Configuration"
            description="Select terms to configure a custom price for your package’s Demographics and Add-ons."
        >
            <Label>Terms</Label>
            <div className="flex space-x-6">
                <RadioInput
                    checked={!isAdvanceSelected}
                    onChange={() => {
                        setFieldValue("basicDiscount.termType", TermsType.BASIC);
                        setIsAdvanceSelected(false);
                    }}
                >
                    Basic
                </RadioInput>
                <RadioInput
                    checked={isAdvanceSelected}
                    onChange={() => {
                        setFieldValue("basicDiscount.termType", TermsType.ADVANCED);
                        setIsAdvanceSelected(true);
                    }}
                >
                    Advanced
                </RadioInput>
            </div>
        </ContentLayout.Section>
    );
};
